import React, { useState, useEffect, useCallback, useRef } from 'react';
import SplashScreen from './components/SplashScreen';
import { SplashScreen as CapSplash } from '@capacitor/splash-screen';
import { BrowserRouter as Router, useLocation, Route, Routes, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ChatPage from './pages/ChatPage';
import ImagePage from './pages/ImagePage';
import AssistantsPage from './pages/AssistantsPage';
import CompanionChatPage from './pages/CompanionChatPage';
import AdminPage from './pages/AdminPage';
import LoginModal from './components/LoginModal';
import MeshBackground from './components/MeshBackground';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { UsageProvider, useUsage } from './contexts/UsageContext';
import { subscribe as billingSubscribe } from './lib/billing';
import { listChats, deleteChat as apiDeleteChat, newChatId, clearAllChats, saveChat, claimAnonChats } from './lib/chats';
import { claimAnonCompanions, listCompanions } from './lib/companions';
import { getAnonUserId, getAllLocalChats, clearAllLocalChats } from './lib/localChats';
import { ACTIVE_ASSISTANT_KEY } from './pages/AssistantsPage';
import './App.css';

const MOBILE_QUERY = '(max-width: 768px)';

// Inner shell — the bit that needs to be inside <Router> so it can use
// useLocation() to decide which page is "active". The actual page
// components are *always* mounted; only their `active` prop changes on
// route change. This is what keeps an in-progress chat or in-flight
// image batch alive when the user clicks over to the other tab.
function AppShellInner({
  user, signOut, setUser, isMobile, mobileOpen, setMobileOpen,
  loginOpen, requestLogin, closeLogin,
  loginIntent, setLoginIntent,
  chats, companions, activeChatId, chatsLoading,
  handleNewChat, handleSelectChat, handleDeleteChat, handleClearAllChats,
  onChatPersisted, onChatRenamed,
}) {
  const { pathname } = useLocation();
  // pathname is the source of truth for which page is "visible". The
  // pages still render their own .page wrapper; they take the `active`
  // prop and either apply the `on` class (visible) or omit it (display:
  // none via App.css). State stays mounted either way.
  const onRoute = (p) => pathname === p;
  // True for the companion-chat route (/mitras/<id>). Sidebar should
  // collapse its "Recent" list since that view has its own header.
  const onMitraChat = pathname.startsWith('/mitras/') && pathname.length > '/mitras/'.length;

  const { refresh: refreshUsage } = useUsage();

  // Subscribe flow — clicked from anywhere (UsageMeter, QuotaModal).
  // If signed in: hit the stub endpoint and refresh user + usage.
  // If anon:     pop LoginModal in 'signup-then-subscribe' mode; the
  //              login success effect upstream then completes the sub.
  const handleSubscribe = useCallback(async () => {
    if (!user) {
      setLoginIntent('subscribe');
      requestLogin();
      return;
    }
    try {
      await billingSubscribe();
      // Pull /auth/me again so user.plan is fresh on the client.
      const { whoAmI } = await import('./lib/auth');
      const me = await whoAmI();
      if (me) setUser(me);
      await refreshUsage();
    } catch (err) {
      console.warn('[subscribe]', err.message);
      window.alert(err.message || 'Subscription failed.');
    }
  }, [user, setUser, requestLogin, setLoginIntent, refreshUsage]);

  // Right after sign-in, if the user originally clicked Subscribe,
  // chain straight into the subscribe call so the flow is one tap.
  const prevUserForSubRef = useRef(null);
  useEffect(() => {
    const justSignedIn = !prevUserForSubRef.current && user;
    prevUserForSubRef.current = user;
    if (justSignedIn && loginIntent === 'subscribe') {
      setLoginIntent(null);
      handleSubscribe();
    }
  }, [user, loginIntent, setLoginIntent, handleSubscribe]);

  // /admin is a fully standalone page — no sidebar, no Header, no
  // mesh background. Short-circuit AFTER all hooks have run (React
  // rules-of-hooks) but BEFORE the regular-shell chrome renders. The
  // admin's session token is the same one /auth/login mints; AdminPage
  // manages its own login UI.
  if (pathname.startsWith('/admin')) {
    return <AdminPage />;
  }

  return (
    <div id="shell">
      {isMobile && mobileOpen && (
        <div id="sb-overlay" className="show" onClick={() => setMobileOpen(false)} />
      )}

      <Sidebar
        isOpen={mobileOpen}
        isMobile={isMobile}
        onMobileClose={() => setMobileOpen(false)}
        chats={chats}
        companions={companions}
        activeChatId={activeChatId}
        chatsLoading={chatsLoading}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        onClearAllChats={handleClearAllChats}
        onRenameChat={onChatRenamed}
        user={user}
        signOut={signOut}
        onRequestLogin={requestLogin}
        onSubscribe={handleSubscribe}
      />

      <div className="main">
        <Header
          isMobile={isMobile}
          onToggleMobileSidebar={() => setMobileOpen((v) => !v)}
          onSubscribe={handleSubscribe}
          onRequestLogin={requestLogin}
        />

        {/* All three pages mount once. Only the active one gets the `on`
            class (CSS shows it, hides the others). This is what fixes
            "switch tab → chat is gone" — the chat component never
            unmounts when the user clicks /image-generator. */}
        <ChatPage
          active={onRoute('/')}
          activeChatId={activeChatId}
          onChatPersisted={onChatPersisted}
          mintChatId={newChatId}
          onRequestLogin={requestLogin}
          onSubscribe={handleSubscribe}
        />
        <ImagePage
          active={onRoute('/image-generator')}
          onSubscribe={handleSubscribe}
        />
        <AssistantsPage
          active={onRoute('/assistants')}
          onStartChat={handleNewChat}
          onRequestLogin={requestLogin}
        />
        {/* Character chat is a parametric route, so it can't ride the
            "all pages mounted" pattern — we mount it via <Routes> with
            its own URL match. Lifecycle resets per companion id, which
            is what we want for a fresh thread / memory state. The bare
            /mitras path redirects to the Characters tab — the old
            CompanionsPage listing has been removed. */}
        <Routes>
          <Route path="/mitras" element={<Navigate to="/assistants" replace />} />
          <Route
            path="/mitras/:id"
            element={
              <CompanionChatPage
                active={onMitraChat}
                onSubscribe={handleSubscribe}
              />
            }
          />
        </Routes>
      </div>

      {loginOpen && <LoginModal onClose={closeLogin} setUser={setUser} />}
    </div>
  );
}

function AppShell() {
  const { user, loading, signOut, setUser } = useAuth();
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(MOBILE_QUERY).matches
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  // Login modal opens on-demand: when the user clicks "Sign in" or
  // when an anon user clicks Subscribe (forced sign-up first).
  const [loginOpen, setLoginOpen] = useState(false);
  // 'subscribe' = post-login, chain into billingSubscribe(). null = no
  // chained action. Lives on AppShell so it survives a render cycle
  // between requestLogin() and the user object actually showing up.
  const [loginIntent, setLoginIntent] = useState(null);
  const requestLogin = useCallback(() => setLoginOpen(true), []);
  const closeLogin = useCallback(() => setLoginOpen(false), []);
  // Auto-close once the signed-in user lands.
  useEffect(() => { if (user) setLoginOpen(false); }, [user]);

  // Configure the native status bar and add top padding for the notch/status bar.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const isDark = theme === 'dark';
      // Style.Dark = light text on dark bg, Style.Light = dark text on light bg
      StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light }).catch(() => {});
      StatusBar.setBackgroundColor({ color: isDark ? '#020508' : '#fafaf8' }).catch(() => {});
      // Android WebView doesn't support env(safe-area-inset-top), so we
      // detect the actual status bar height from the screen vs viewport diff.
      // On iOS we use the same logic to keep spacing consistent across platforms.
      const statusBarHeight = window.screen.height - window.innerHeight > 100
        ? Math.round((window.screen.height - window.innerHeight) * 0.3)
        : 36; // reasonable default for modern status bars
      document.documentElement.style.setProperty('--sat', statusBarHeight + 'px');
    }
  }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [chatsLoading, setChatsLoading] = useState(false);
  // Character sessions surfaced in the sidebar's "Recent" list so users
  // can resume any conversation in one click — these live in the
  // aj-companions table, which the regular chat history (aj-deepai-chats)
  // wouldn't otherwise touch.
  const [companions, setCompanions] = useState([]);

  // Mint the anonymous user id once on first load. Doesn't gate
  // anything — just gives the server a stable handle to key the
  // anon's chats / quota under (DeepAI's `display_user_id` pattern).
  useEffect(() => { try { getAnonUserId(); } catch {} }, []);

  // One-time sync of any pre-existing localStorage chats (from before
  // the server-only switch) up to the server under this browser's
  // anon UID. After the push we delete the local keys and stamp
  // arthum.localSync.v1 so this never runs again on this device.
  useEffect(() => {
    try {
      if (localStorage.getItem('arthum.localSync.v1') === 'done') return;
      const local = getAllLocalChats();
      if (!local.length) {
        localStorage.setItem('arthum.localSync.v1', 'done');
        return;
      }
      (async () => {
        let migrated = 0;
        for (const c of local) {
          try {
            await saveChat(c.chatId, {
              title: c.title,
              messages: c.messages,
              scope: c.scope,
              assistantId: c.assistantId,
              assistantName: c.assistantName,
            });
            migrated += 1;
          } catch (err) {
            console.warn('[migrate]', c.chatId, err.message);
          }
        }
        if (migrated === local.length) {
          // All in — safe to drop the local copies. If even one
          // failed we leave the local store intact so a retry on
          // the next load can still pick them up.
          clearAllLocalChats();
          localStorage.setItem('arthum.localSync.v1', 'done');
        }
        // Reload the sidebar with whatever's on the server now.
        refreshChatsRef.current?.();
      })();
    } catch (err) {
      console.warn('[migrate-init]', err.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshChats = useCallback(async () => {
    setChatsLoading(true);
    try {
      // Server is the only source of truth now. listChats() sends a
      // Bearer token (signed-in) or X-Anon-Uid header (anon). Fetch
      // companions in parallel so the sidebar can surface character
      // sessions alongside regular chats.
      const [c, m] = await Promise.all([
        listChats(),
        listCompanions().catch(() => []),
      ]);
      setChats(c);
      setCompanions(m || []);
    }
    catch (err) { console.warn('[chats list]', err.message); }
    finally { setChatsLoading(false); }
  }, [user]);

  // Refresh whenever auth state flips. The migration effect above
  // also reaches for refreshChats via this ref, so it can fire after
  // a successful local→server push without forming a hook-deps loop.
  const refreshChatsRef = useRef(refreshChats);
  useEffect(() => { refreshChatsRef.current = refreshChats; }, [refreshChats]);
  useEffect(() => { refreshChats(); }, [refreshChats]);

  // Sign-in transition: when `user` goes null → object, claim any
  // chats this browser created under its anon UID and merge them
  // into the new account. Server transfers PKs + strips TTL. Then
  // refresh so the sidebar shows the newly-permanent chats.
  const prevUserRef = useRef(null);
  useEffect(() => {
    const justSignedIn = !prevUserRef.current && user;
    prevUserRef.current = user;
    if (!justSignedIn) return;
    (async () => {
      try {
        const [chatsOut, mitrasOut] = await Promise.allSettled([
          claimAnonChats(), claimAnonCompanions(),
        ]);
        const chatsClaimed = chatsOut.status === 'fulfilled' ? chatsOut.value?.claimed : 0;
        if (chatsClaimed > 0) refreshChats();
        if (mitrasOut.status === 'rejected') {
          console.warn('[claim-mitras]', mitrasOut.reason?.message);
        }
      } catch (err) {
        console.warn('[claim]', err.message);
      }
    })();
  }, [user, refreshChats]);

  const handleNewChat = () => {
    setActiveChatId(null);
    // "+ New chat" should ALSO drop any active assistant persona.
    // Without this, the user picks Astro/Chanakya on /assistants, then
    // hits + New chat expecting a fresh regular chat — but the persona
    // is still applied and their first message gets tagged as another
    // assistant chat. Clear the stored assistant + ping ChatPage via
    // the same custom event AssistantsPage uses to swap personas.
    try {
      localStorage.removeItem(ACTIVE_ASSISTANT_KEY);
      // Also wipe the in-progress chat draft so the new chat starts
      // blank rather than rehydrating the previous thread.
      localStorage.removeItem('arthum.chatDraft.v1');
      window.dispatchEvent(new Event('arthum:assistant-changed'));
    } catch { /* SSR / disabled storage */ }
    if (isMobile) setMobileOpen(false);
  };

  const handleSelectChat = (id) => {
    setActiveChatId(id);
    if (isMobile) setMobileOpen(false);
  };

  const handleDeleteChat = async (id) => {
    try { await apiDeleteChat(id); }
    catch (err) { console.warn('[chat delete]', err.message); }
    setChats((cs) => cs.filter((c) => c.chatId !== id));
    if (activeChatId === id) setActiveChatId(null);
  };

  // Bulk-clear chats matching the current tab's scope. Called by the
  // Sidebar "Clear all" link with scope='chat' or scope='assistant'.
  const handleClearAllChats = useCallback(async (scope) => {
    try { await clearAllChats({ scope }); }
    catch (err) { console.warn('[chats clear]', err.message); }
    // Drop the matching rows from local state immediately. Legacy
    // untagged rows go under 'chat' to match list/scope semantics.
    setChats((cs) => cs.filter((c) => {
      if (scope === 'assistant') return c.scope !== 'assistant';
      if (scope === 'chat')      return c.scope === 'assistant';
      return false;   // no scope = wipe everything
    }));
    setActiveChatId(null);
  }, []);

  const onChatPersisted = useCallback((meta) => {
    setActiveChatId(meta.chatId);
    setChats((cs) => {
      const without = cs.filter((c) => c.chatId !== meta.chatId);
      // Carry forward scope/assistant fields the backend echoed back.
      // Per-tab sidebar filtering in Sidebar.js depends on these.
      return [{
        chatId: meta.chatId,
        title: meta.title,
        updatedAt: meta.updatedAt,
        createdAt: meta.createdAt,
        scope: meta.scope,
        assistantId: meta.assistantId,
        assistantName: meta.assistantName,
      }, ...without];
    });
  }, []);

  // Optimistic local rename — sidebar updates instantly, backend
  // catches up in the background. Reverts to the server value if the
  // PATCH fails.
  const onChatRenamed = useCallback((chatId, newTitle) => {
    setChats((cs) => cs.map((c) =>
      c.chatId === chatId ? { ...c, title: newTitle, updatedAt: Date.now() } : c
    ));
  }, []);

  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    CapSplash.hide().catch(() => {});
  }, []);

  if (loading || !splashDone) {
    return <SplashScreen ready={!loading} onFinish={() => setSplashDone(true)} />;
  }

  return (
    <Router>
      <MeshBackground />
      <UsageProvider user={user}>
        <AppShellInner
          user={user}
          signOut={signOut}
          setUser={setUser}
          isMobile={isMobile}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          loginOpen={loginOpen}
          requestLogin={requestLogin}
          closeLogin={closeLogin}
          loginIntent={loginIntent}
          setLoginIntent={setLoginIntent}
          chats={chats}
          companions={companions}
          activeChatId={activeChatId}
          chatsLoading={chatsLoading}
          handleNewChat={handleNewChat}
          handleSelectChat={handleSelectChat}
          handleDeleteChat={handleDeleteChat}
          handleClearAllChats={handleClearAllChats}
          onChatPersisted={onChatPersisted}
          onChatRenamed={onChatRenamed}
        />
      </UsageProvider>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
